import { useEffect, useState, useCallback, useRef } from "react";
import Modal from "shared/Modal";
import Button from "shared/Button";
import { Loader2 } from "lucide-react";
import {
  LeaveRequestItem, LEAVE_TYPE_LABELS,
  fetchMyLeaveRequests, fetchAllLeaveRequests, cancelLeaveRequest, reviewLeaveRequest,
} from "../api/leaveApi";
import LeaveRequestModal from "./LeaveRequestModal";
import { AttendanceEmployee } from "../api/attendanceApi";
import { REQUEST_STATUS_STYLES, REQUEST_STATUS_FILTERS } from "./requestStatusStyles";
import ReviewControls from "./ReviewControls";

interface Props {
  role: "admin" | "employee";
  // admin only — passed through to the nested "Add Leave" modal so it
  // doesn't have to fetch its own copy of the employee list.
  employees?: AttendanceEmployee[];
  onClose: () => void;
  onSuccess?: () => void;
}

function formatDateRange(start: string, end: string) {
  return start === end ? start : `${start} – ${end}`;
}

export default function LeaveRequestsPanel({ role, employees, onClose, onSuccess }: Props) {
  const [items, setItems] = useState<LeaveRequestItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [showAddLeave, setShowAddLeave] = useState(false);

  // Tracks any in-flight withdraw/review across the whole list (including
  // each row's own ReviewControls instance) so the panel can refuse to
  // close mid-request — matches AttendanceActionModal/LeaveRequestModal,
  // which both guard their own close the same way while submitting.
  // Ref-counted (not a single boolean) since multiple rows can be busy at
  // once (e.g. a withdraw and a different row's review, both in flight).
  const activeOpsRef = useRef(0);
  const [hasActiveOps, setHasActiveOps] = useState(false);
  const beginOp = useCallback(() => {
    activeOpsRef.current += 1;
    setHasActiveOps(true);
  }, []);
  const endOp = useCallback(() => {
    activeOpsRef.current = Math.max(0, activeOpsRef.current - 1);
    if (activeOpsRef.current === 0) setHasActiveOps(false);
  }, []);

  // Concurrent actions on different rows (e.g. approving A while rejecting
  // B) each trigger their own load() — if their responses arrive out of
  // order, an older one resolving after a newer one would otherwise
  // overwrite the list with stale data. Only the most-recently-*started*
  // load's response is ever applied.
  const loadSeqRef = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const data = role === "admin"
        ? await fetchAllLeaveRequests(statusFilter || undefined)
        : await fetchMyLeaveRequests(statusFilter || undefined);
      if (seq === loadSeqRef.current) setItems(data.items || []);
    } catch (err) {
      console.error("Failed to load leave requests:", err);
    }
    if (seq === loadSeqRef.current) setLoading(false);
  }, [role, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleWithdraw = async (id: number) => {
    setBusyId(id);
    beginOp();
    try {
      await cancelLeaveRequest(id);
      await load();
      onSuccess?.();
    } catch (err: any) {
      alert(err?.response?.data?.detail || "Failed to withdraw request.");
    }
    setBusyId(null);
    endOp();
  };

  const handleReview = async (id: number, decision: "approve" | "reject", note?: string) => {
    beginOp();
    try {
      await reviewLeaveRequest(id, decision, note);
      await load();
      onSuccess?.();
    } catch (err: any) {
      alert(err?.response?.data?.detail || "Failed to review request.");
    }
    endOp();
  };

  return (
    <>
      <Modal size="lg" onClick={hasActiveOps ? undefined : onClose} closeButtonCLick={hasActiveOps ? undefined : onClose} modalClassName="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground font-inter">{role === "admin" ? "Leave Requests" : "My Leave Requests"}</h2>
          {role === "admin" && (
            <Button size="sm" variant="primary" onClick={() => setShowAddLeave(true)}>
              Add Leave
            </Button>
          )}
        </div>

        <div className="flex gap-2 mb-4">
          {REQUEST_STATUS_FILTERS.map((s) => (
            <button
              key={s || "all"}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs font-inter rounded-lg transition-colors ${
                statusFilter === s ? "bg-primary text-primary-foreground" : "bg-surface-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {s ? s[0].toUpperCase() + s.slice(1) : "All"}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-5 h-5 text-primary animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground font-inter text-center py-10">No leave requests found.</p>
        ) : (
          <div className="space-y-3">
            {items.map((r) => (
              <div key={r.id} className="border border-border rounded-xl p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-foreground font-inter">
                      {LEAVE_TYPE_LABELS[r.leave_type] || r.leave_type} · {formatDateRange(r.start_date, r.end_date)}
                    </p>
                    {role === "admin" && (
                      <p className="text-xs text-muted-foreground font-inter mt-0.5">{r.employee_name} · {r.employee_code}</p>
                    )}
                    <p className="text-xs text-muted-foreground font-inter mt-1">"{r.reason}"</p>
                    {r.review_note && (
                      <p className="text-xs text-muted-foreground font-inter mt-1">Review note: {r.review_note}</p>
                    )}
                    {r.reviewed_by && (
                      <p className="text-xs text-muted-foreground font-inter mt-1">Reviewed by {r.reviewed_by}</p>
                    )}
                  </div>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium font-inter whitespace-nowrap ${REQUEST_STATUS_STYLES[r.status]}`}
                  >
                    {r.status}
                  </span>
                </div>

                {role === "employee" && r.status === "pending" && (
                  <div className="mt-3">
                    <Button size="sm" variant="outline" disabled={busyId === r.id} onClick={() => handleWithdraw(r.id)}>
                      {busyId === r.id ? "Withdrawing…" : "Withdraw"}
                    </Button>
                  </div>
                )}

                {role === "admin" && r.status === "pending" && (
                  <div className="mt-3">
                    <ReviewControls
                      onApprove={(note) => handleReview(r.id, "approve", note)}
                      onReject={(note) => handleReview(r.id, "reject", note)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>

      {showAddLeave && (
        <LeaveRequestModal
          writeMode="admin-direct"
          employees={employees}
          onClose={() => setShowAddLeave(false)}
          onSuccess={() => {
            // admin_create_leave_request is always pre-approved server-side.
            // "" (All) and "approved" already show it without changing
            // anything — only "pending"/"rejected" would actually hide the
            // new item, so only those need to switch tabs (narrowing the
            // admin's view from "All" to "approved" just to reveal an item
            // "All" was already showing would hide whatever else they were
            // looking at for no reason). setStatusFilter is a no-op reload
            // when the value doesn't change (e.g. already "approved"), so
            // those two cases need an explicit reload instead.
            if (statusFilter === "" || statusFilter === "approved") {
              load();
            } else {
              setStatusFilter("approved");
            }
            onSuccess?.();
          }}
        />
      )}
    </>
  );
}
